/**
 * Check-in document API slice (RTK). Same pattern as workingDocumentSlice / objectsSlice.
 * Thunk only: get token, call API, return or reject. Slice handles pending/fulfilled/rejected.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserAuth from "../../../auth/userAuth";
import AuthTeams from "../../../auth/AuthTeams";
import { checkinAPI, isBrowseFileBlob } from "../../api/checkinAPI";
import {
  sanitizeFileName,
  getDocClassCodeOnly,
} from "../../../functions/helperMethod";
import { isTokenRefreshFailed } from "../../../functions/reduxMethod";
import { logger } from "../../../utils/logger";
import { REFRESH_TOKEN_ACTION } from "../../constants/metaData";

const userAuth = new UserAuth();

/** Client-only field carrying the browsed File/Blob for chunked upload. */
export const BROWSE_FILE_FIELD = "__browseFile";

function resolveDocFormat(providedFormat, fileName, formatRequired) {
  if (providedFormat != null && String(providedFormat).trim() !== "") {
    return String(providedFormat).trim();
  }
  if (formatRequired !== true) return "";
  if (typeof fileName !== "string") return "";
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot < 0 || lastDot === fileName.length - 1) return "";
  return fileName
    .substring(lastDot + 1)
    .trim()
    .toUpperCase();
}

function stripBrowseFile(doc) {
  if (!doc || typeof doc !== "object") return { body: doc, browseFile: null };
  const browseFile = isBrowseFileBlob(doc[BROWSE_FILE_FIELD])
    ? doc[BROWSE_FILE_FIELD]
    : null;
  if (!browseFile) {
    return { body: doc, browseFile: null };
  }
  const body = { ...doc };
  delete body[BROWSE_FILE_FIELD];
  body.fileData = "";
  delete body.fileUrl;
  return { body, browseFile };
}

function toBulkItemResult(body, res, errorMessage) {
  const data = res?.data;
  return {
    fileName: body?.fileName,
    title: body?.title,
    oneDriveDocumentId: body?.oneDriveDocumentId,
    statusCode: res?.status ?? 400,
    docNo: data?.docNo ?? data?.DocNo,
    docSheet: data?.docSheet ?? data?.DocSheet,
    docRev: data?.docRev ?? data?.DocRev,
    docClass: data?.docClass ?? data?.DocClass,
    errorMessage: errorMessage || undefined,
  };
}

export const checkinDocumentsBulkThunk = createAsyncThunk(
  "checkinApi/checkinDocumentsBulk",
  async ({ documents }, { rejectWithValue }) => {
    const list = Array.isArray(documents) ? documents : [];
    logger.info(
      "checkinDocumentsBulkThunk start",
      { count: list.length },
      "REDUX",
    );
    const tokenResponse = await userAuth.getUserToken(true);
    const tokenOneDrive = tokenResponse ? tokenResponse.accessToken : null;
    if (!tokenOneDrive) {
      return rejectWithValue({
        message: "No access token",
        code: "UNAUTHORIZED",
      });
    }
    if (list.length === 0) {
      return rejectWithValue({ message: "No documents to check in." });
    }

    try {
      const browseEntries = [];
      const remoteEntries = [];
      for (const doc of list) {
        const { body, browseFile } = stripBrowseFile(doc);
        if (browseFile) {
          browseEntries.push({ body, browseFile });
        } else {
          remoteEntries.push(body);
        }
      }

      // All remote OneDrive docs can still use the bulk JSON endpoint.
      if (browseEntries.length === 0) {
        const res = await checkinAPI.checkinDocumentsBulk(
          tokenOneDrive,
          remoteEntries,
        );
        if (res.status !== 200 && res.status !== 201) {
          return rejectWithValue({ status: res.status, data: res.data });
        }
        logger.info("checkinDocumentsBulkThunk success", null, "REDUX");
        return { data: res.data, status: res.status };
      }

      // Share-style: browse files upload via binary chunks (one file at a time).
      const results = [];
      for (const { body, browseFile } of browseEntries) {
        try {
          const res = await checkinAPI.checkinDocumentChunked(
            tokenOneDrive,
            body,
            browseFile,
          );
          results.push(toBulkItemResult(body, res));
        } catch (error) {
          results.push(
            toBulkItemResult(
              body,
              { status: error?.response?.status ?? 400 },
              error?.message ?? "Chunked check-in failed.",
            ),
          );
        }
      }

      if (remoteEntries.length > 0) {
        const res = await checkinAPI.checkinDocumentsBulk(
          tokenOneDrive,
          remoteEntries,
        );
        const remoteResults = Array.isArray(res?.data?.results)
          ? res.data.results
          : Array.isArray(res?.data?.Results)
            ? res.data.Results
            : [];
        results.push(...remoteResults);
      }

      const successCount = results.filter(
        (item) => Number(item.statusCode ?? item.StatusCode) === 200,
      ).length;
      const failureCount = results.length - successCount;
      const data = {
        results,
        successCount,
        failureCount,
      };
      const status =
        failureCount === 0 ? 200 : successCount > 0 ? 201 : 400;
      if (status === 400) {
        return rejectWithValue({
          status,
          data,
          message:
            results.find((r) => r.errorMessage)?.errorMessage ??
            "Bulk check-in failed.",
        });
      }
      logger.info(
        "checkinDocumentsBulkThunk success",
        { successCount, failureCount },
        "REDUX",
      );
      return { data, status };
    } catch (error) {
      logger.error("checkinDocumentsBulkThunk failed", error, "REDUX");
      const isTimeout =
        error?.code === "ECONNABORTED" ||
        /timeout/i.test(String(error?.message ?? ""));
      return rejectWithValue({
        message: isTimeout
          ? "Check-in timed out. Please try again."
          : (error?.message ?? "Request failed"),
        code: isTimeout ? "TIMEOUT" : error?.response?.status,
        data: error?.response?.data,
      });
    }
  },
);


export const checkinExistingDocumentsBulkThunk = createAsyncThunk(
  "checkinApi/checkinExistingDocumentsBulk",
  async ({ documents }, { rejectWithValue }) => {
    const list = Array.isArray(documents) ? documents : [];
    logger.info(
      "checkinExistingDocumentsBulkThunk start",
      { count: list.length },
      "REDUX",
    );
    const tokenResponse = await userAuth.getUserToken(true);
    const tokenOneDrive = tokenResponse ? tokenResponse.accessToken : null;
    if (!tokenOneDrive) {
      return rejectWithValue({
        message: "No access token",
        code: "UNAUTHORIZED",
      });
    }
    if (list.length === 0) {
      return rejectWithValue({ message: "No documents to check in." });
    }

    const results = [];
    for (const entry of list) {
      const meta = entry?.__meta ?? {};
      const viewCopy = entry?.__viewCopy ?? null;
      const body = { ...entry };
      delete body.__meta;
      delete body.__viewCopy;
      logger.info(
        "checkinExistingDocumentsBulkThunk POST",
        {
          docClass: body.docClass,
          docNo: body.docNo,
          docRev: body.docRev,
          docSheet: body.docSheet,
          docFormat: body.docFormat,
          fileName: body.fileName,
          fileDataLen:
            typeof body.fileData === "string" ? body.fileData.length : 0,
          createViewCopy: body.createViewCopy,
          status: body.status,
        },
        "REDUX",
      );
      try {
        const res = await checkinAPI.checkinExistingDocument(
          tokenOneDrive,
          body,
        );
        logger.info(
          "checkinExistingDocumentsBulkThunk response",
          { docNo: body.docNo, status: res?.status },
          "REDUX",
        );
        const ok = res.status === 200;
       
        if (ok && viewCopy?.base64str) {
          try {
            await checkinAPI.checkinDocument(tokenOneDrive, {
              checkinViewCopy: true,
              docClass: body.docClass,
              docNo: body.docNo,
              docSheet: body.docSheet,
              docRev: body.docRev,
              title: `${viewCopy.title ?? ""} View Copy`,
              fileData: viewCopy.base64str,
              fileName: viewCopy.name,
              rollbackRevisionOnFail: viewCopy.viewRequired === true,
              createViewCopy: false,
              webUser: body.webUser,
              status: body.status,
            });
          } catch (vcErr) {
            logger.warn(
              "checkinExistingDocumentsBulkThunk: view copy upload failed",
              vcErr,
              "REDUX",
            );
          }
        }
        results.push({
          ...meta,
          fileName: body?.fileName,
          title: body?.title,
          statusCode: res?.status ?? 400,
          data: res?.data,
          errorMessage: ok
            ? undefined
            : res.status === 203
              ? "Document is already checked out."
              : "Check-in failed.",
        });
      } catch (error) {
        logger.error(
          "checkinExistingDocumentsBulkThunk POST failed",
          {
            docNo: body.docNo,
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
          },
          "REDUX",
        );
        results.push({
          ...meta,
          fileName: body?.fileName,
          title: body?.title,
          statusCode: error?.response?.status ?? 400,
          errorMessage:
            error?.response?.data?.message ??
            (typeof error?.response?.data === "string"
              ? error.response.data
              : null) ??
            error?.message ??
            "Check-in failed.",
        });
      }
    }

    const successCount = results.filter(
      (item) => Number(item.statusCode) === 200,
    ).length;
    const failureCount = results.length - successCount;
    logger.info(
      "checkinExistingDocumentsBulkThunk done",
      { successCount, failureCount },
      "REDUX",
    );
    return {
      data: { results, successCount, failureCount },
      status: failureCount === 0 ? 200 : successCount > 0 ? 201 : 400,
    };
  },
);

export const checkinDocumentThunk = createAsyncThunk(
  "checkinApi/checkinDocument",
  async (
    {
      docTitle,
      docClass,
      docFormat,
      formatRequired,
      uploadFiles,
      fileUrl,
      createViewCopy,
      selectedObjects,
      uploadViewCopyCount,
      viewRequired,
      docReleaseStatus,
      oneDriveDeleteRequired,
      restrictedAccess,
      tokenRefreshPreAction = {
        retry: 0,
        actionType: REFRESH_TOKEN_ACTION.checkinNew,
        refreshResponse: "success",
      },
    },
    { rejectWithValue },
  ) => {
    const docClassCode = getDocClassCodeOnly(docClass ?? "");
    logger.info(
      "checkinDocumentThunk start",
      { docClass: docClassCode, docTitle },
      "REDUX",
    );
    const tokenResponse = await userAuth.getUserToken(true);
    const tokenOneDrive = tokenResponse ? tokenResponse.accessToken : null;
    if (!tokenOneDrive) {
      return rejectWithValue({
        message: "No access token",
        code: "UNAUTHORIZED",
      });
    }
    if (isTokenRefreshFailed(tokenRefreshPreAction)) {
      return rejectWithValue({ message: "Token refresh failed" });
    }

    const selectedList = Array.isArray(selectedObjects) ? selectedObjects : [];
    const uploadFilesList = Array.isArray(uploadFiles) ? uploadFiles : [];
    const objConData = selectedList.map((item) => ({
      luName: item.luName,
      keyRef: item.keyRef,
      updateRevision: "",
      updateRevisionCode: "",
      KeepLastDocRev: "LatestRevision",
      SurveyLockedFlag: "Unlocked",
      SurveyLockedFlagDb: "0",
      KeepLastDocRevDb: "L",
    }));

    const webUser = new AuthTeams().getEmail();
    const status =
      docReleaseStatus === false
        ? ""
        : uploadViewCopyCount > 0
          ? ""
          : docReleaseStatus;

    let lastResult = null;
    try {
      for (let i = 0; i < uploadFilesList.length; i++) {
        const uploadFile = uploadFilesList[i];
        const resolvedDocFormat = resolveDocFormat(
          docFormat,
          uploadFile?.name,
          formatRequired,
        );
        const browseFile = isBrowseFileBlob(uploadFile?.file)
          ? uploadFile.file
          : isBrowseFileBlob(uploadFile?.[BROWSE_FILE_FIELD])
            ? uploadFile[BROWSE_FILE_FIELD]
            : null;
        const fileData =
          typeof uploadFile?.base64str === "string" ? uploadFile.base64str : "";
        const shouldUseFileUrl =
          !browseFile &&
          typeof fileUrl === "string" &&
          fileUrl.trim() !== "" &&
          fileData.trim() === "";
        const docInfo = {
          checkinViewCopy: false,
          docClass: docClassCode,
          title: docTitle,
          docFormat: resolvedDocFormat,
          fileData: browseFile ? "" : fileData,
          // When check-in is initiated from OneDrive, frontend can omit base64 and send a URL instead.
          ...(shouldUseFileUrl ? { fileUrl: fileUrl.trim() } : {}),
          fileName: sanitizeFileName(uploadFile.name),
          objectConnections: objConData,
          createViewCopy,
          rollbackRevisionOnFail: viewRequired,
          restrictedAccess:
            restrictedAccess === true || `${restrictedAccess}`.toUpperCase() === "Y"
              ? "Y"
              : "N",
          webUser,
          status,
        };
        const res = browseFile
          ? await checkinAPI.checkinDocumentChunked(
              tokenOneDrive,
              docInfo,
              browseFile,
            )
          : await checkinAPI.checkinDocument(tokenOneDrive, docInfo);
        if (res.status !== 200) {
          return rejectWithValue({ status: res.status, data: res.data });
        }
        lastResult = res;
      }
      logger.info("checkinDocumentThunk success", null, "REDUX");
      return lastResult
        ? { data: lastResult.data, status: lastResult.status }
        : { data: null, status: 200 };
    } catch (error) {
      logger.error("checkinDocumentThunk failed", error, "REDUX");
      const isTimeout =
        error?.code === "ECONNABORTED" ||
        /timeout/i.test(String(error?.message ?? ""));
      return rejectWithValue({
        message: isTimeout
          ? "Check-in timed out. Please try again."
          : (error?.message ?? "Request failed"),
        code: isTimeout ? "TIMEOUT" : error?.response?.status,
      });
    }
  },
);

const initialState = {
  checkinLoading: false,
  checkinBulkLoading: false,
  checkinExistingBulkLoading: false,
  checkinError: null,
};

const checkinApiSlice = createSlice({
  name: "checkinApi",
  initialState,
  reducers: {
    clearCheckinError: (state) => {
      state.checkinError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkinDocumentThunk.pending, (state) => {
        state.checkinLoading = true;
        state.checkinError = null;
      })
      .addCase(checkinDocumentThunk.fulfilled, (state) => {
        state.checkinLoading = false;
        state.checkinError = null;
      })
      .addCase(checkinDocumentThunk.rejected, (state, action) => {
        state.checkinLoading = false;
        state.checkinError = action.payload ?? { message: "Unknown error" };
      })
      .addCase(checkinDocumentsBulkThunk.pending, (state) => {
        state.checkinBulkLoading = true;
        state.checkinError = null;
      })
      .addCase(checkinDocumentsBulkThunk.fulfilled, (state) => {
        state.checkinBulkLoading = false;
        state.checkinError = null;
      })
      .addCase(checkinDocumentsBulkThunk.rejected, (state, action) => {
        state.checkinBulkLoading = false;
        state.checkinError = action.payload ?? { message: "Unknown error" };
      })
      .addCase(checkinExistingDocumentsBulkThunk.pending, (state) => {
        state.checkinExistingBulkLoading = true;
        state.checkinError = null;
      })
      .addCase(checkinExistingDocumentsBulkThunk.fulfilled, (state) => {
        state.checkinExistingBulkLoading = false;
        state.checkinError = null;
      })
      .addCase(checkinExistingDocumentsBulkThunk.rejected, (state, action) => {
        state.checkinExistingBulkLoading = false;
        state.checkinError = action.payload ?? { message: "Unknown error" };
      });
  },
});

export const { clearCheckinError } = checkinApiSlice.actions;
export default checkinApiSlice.reducer;
